import { useEffect, useRef } from "react";
import "./Services.css";

const SERVICES = [
  {
    id: "quality-engineering",
    title: "Quality Engineering",
    tagline: "We bug the bugs, so you don't have to.",
    description:
      "We embed quality at every layer, across strategy, process, and code, long before testing begins.",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <linearGradient
            id="grad-qe"
            x1="0"
            y1="0"
            x2="24"
            y2="24"
            gradientUnits="userSpaceOnUse"
          >
            <stop offset="0%" stopColor="#38bdf8" />
            <stop offset="60%" stopColor="#a78bfa" />
            <stop offset="100%" stopColor="#f472b6" />
          </linearGradient>
        </defs>
        <path
          d="M12 2L3 6.5v6c0 4.75 3.87 9.18 9 10.5 5.13-1.32 9-5.75 9-10.5v-6L12 2z"
          stroke="url(#grad-qe)"
          strokeWidth="1.6"
          strokeLinejoin="round"
        />
        <path
          d="M8.5 12.5l2.5 2.5 5-5"
          stroke="url(#grad-qe)"
          strokeWidth="1.6"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ),
  },
  {
    id: "project-delivery",
    title: "Project Delivery",
    tagline: "Ship and deliver, without the drama.",
    description:
      "Clear ownership, realistic timelines and the habits that keep teams shipping consistently.",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <linearGradient
            id="grad-dm"
            x1="0"
            y1="0"
            x2="24"
            y2="24"
            gradientUnits="userSpaceOnUse"
          >
            <stop offset="0%" stopColor="#38bdf8" />
            <stop offset="60%" stopColor="#a78bfa" />
            <stop offset="100%" stopColor="#f472b6" />
          </linearGradient>
        </defs>
        <path
          d="M5 3h14a2 2 0 012 2v4H3V5a2 2 0 012-2z"
          stroke="url(#grad-dm)"
          strokeWidth="1.6"
          strokeLinejoin="round"
        />
        <path
          d="M3 9h18v12a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"
          stroke="url(#grad-dm)"
          strokeWidth="1.6"
        />
        <path
          d="M9 13h6M9 17h4"
          stroke="url(#grad-dm)"
          strokeWidth="1.6"
          strokeLinecap="round"
        />
      </svg>
    ),
  },
  {
    id: "test-automation",
    title: "Test Automation",
    tagline: "Automate the boring, catch the scary.",
    description:
      "Scalable test automation that catches issues early, improves stability and speeds up releases.",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <linearGradient
            id="grad-ta"
            x1="0"
            y1="0"
            x2="24"
            y2="24"
            gradientUnits="userSpaceOnUse"
          >
            <stop offset="0%" stopColor="#38bdf8" />
            <stop offset="60%" stopColor="#a78bfa" />
            <stop offset="100%" stopColor="#f472b6" />
          </linearGradient>
        </defs>
        <rect
          x="2"
          y="3"
          width="20"
          height="18"
          rx="2.5"
          stroke="url(#grad-ta)"
          strokeWidth="1.6"
        />
        <path
          d="M7 8.5l4 3.5-4 3.5"
          stroke="url(#grad-ta)"
          strokeWidth="1.6"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M13 15.5h4"
          stroke="url(#grad-ta)"
          strokeWidth="1.6"
          strokeLinecap="round"
        />
      </svg>
    ),
  },
  {
    id: "product-development",
    title: "Product Development",
    tagline: "Build the right thing, build the thing right.",
    description:
      "From messy vision to structured backlog, we shape ideas into products people actually want.",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <linearGradient
            id="grad-pm"
            x1="0"
            y1="0"
            x2="24"
            y2="24"
            gradientUnits="userSpaceOnUse"
          >
            <stop offset="0%" stopColor="#38bdf8" />
            <stop offset="60%" stopColor="#a78bfa" />
            <stop offset="100%" stopColor="#f472b6" />
          </linearGradient>
        </defs>
        <circle
          cx="12"
          cy="12"
          r="9"
          stroke="url(#grad-pm)"
          strokeWidth="1.6"
        />
        <path
          d="M12 3v9l4.5 4.5"
          stroke="url(#grad-pm)"
          strokeWidth="1.6"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <circle cx="12" cy="12" r="1.75" fill="url(#grad-pm)" />
      </svg>
    ),
  },
  {
    id: "release-management",
    title: "Release Management",
    tagline: "Push the button, not the panic.",
    description:
      "Disciplined pipelines, clear playbooks and coordination that make every release a non-event.",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <linearGradient
            id="grad-rm"
            x1="0"
            y1="0"
            x2="24"
            y2="24"
            gradientUnits="userSpaceOnUse"
          >
            <stop offset="0%" stopColor="#38bdf8" />
            <stop offset="60%" stopColor="#a78bfa" />
            <stop offset="100%" stopColor="#f472b6" />
          </linearGradient>
        </defs>
        <path
          d="M12 3v11"
          stroke="url(#grad-rm)"
          strokeWidth="1.6"
          strokeLinecap="round"
        />
        <path
          d="M8 7l4-4 4 4"
          stroke="url(#grad-rm)"
          strokeWidth="1.6"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M5 17c0-2.76 3.13-5 7-5s7 2.24 7 5"
          stroke="url(#grad-rm)"
          strokeWidth="1.6"
          strokeLinecap="round"
        />
        <path
          d="M3 21h18"
          stroke="url(#grad-rm)"
          strokeWidth="1.6"
          strokeLinecap="round"
        />
      </svg>
    ),
  },
];

export function Services() {
  const rowRefs = useRef([]);

  useEffect(() => {
    const observers = [];

    rowRefs.current.forEach((row) => {
      if (!row) return;

      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            row.classList.add("is-visible");
            observer.disconnect();
          }
        },
        { threshold: 0.2 },
      );

      observer.observe(row);
      observers.push(observer);
    });

    return () => observers.forEach((o) => o.disconnect());
  }, []);

  return (
    <section id="services" className="services">
      <div className="services-inner">
        <header className="services-header">
          <span className="services-label">What we do</span>
          <h2 className="services-title">Services</h2>
          <p className="services-description">
            Discover how we help organizations accelerate their time to market
            by delivering services built on the right mindset and modern
            software development practices.
          </p>
        </header>

        <ol className="services-list">
          {SERVICES.map((service, index) => (
            <li
              key={service.id}
              className="service-row"
              ref={(el) => (rowRefs.current[index] = el)}
              style={{ "--delay": index }}
            >
              <div className="service-row__accent" />
              <span className="service-row__index">
                {String(index + 1).padStart(2, "0")}
              </span>
              <div className="service-row__icon">{service.icon}</div>
              <div className="service-row__body">
                <h3 className="service-row__title">{service.title}</h3>
                <p className="service-row__desc">{service.description}</p>
              </div>
              <p className="service-row__tagline">{service.tagline}</p>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}
